import { createContext, use } from "react";
import type { JSX, ReactNode } from "react";

/** Actions that affect the whole session. */
export interface SessionActions {
  /** Signs out: removes all local personal data (spec 11.4) and ends the session. */
  readonly signOut: () => Promise<void>;
}

const SessionActionsContext = createContext<SessionActions | null>(null);

/** Properties of {@link SessionActionsProvider}. */
export interface SessionActionsProviderProps {
  /** Actions composed in the composition root. */
  readonly actions: SessionActions;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides the session actions.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function SessionActionsProvider(props: SessionActionsProviderProps): JSX.Element {
  const provider = (
    <SessionActionsContext value={props.actions}>{props.children}</SessionActionsContext>
  );

  return provider;
}

/**
 * Returns the session actions.
 *
 * @returns The actions.
 * @throws Error if no {@link SessionActionsProvider} is present.
 */
export function useSessionActions(): SessionActions {
  const actions = use(SessionActionsContext);

  if (actions === null) {
    throw new Error("useSessionActions must be used inside a SessionActionsProvider.");
  }

  return actions;
}
